import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { Hint } from '@/components/cargofl-ui';

export interface LightboxImage {
  id: string;
  url: string;
  filename: string;
}

/**
 * A full-screen viewer for a set of photos.
 *
 * Replaces opening each attachment in its own browser tab. Three photos of one broken
 * laptop are looked at TOGETHER - the point is comparing them - and a tab each means
 * three clicks out, three clicks back, and losing your place on the queue every time.
 *
 * Left and right arrows move; Escape closes. The on-screen chevrons exist because the
 * keyboard shortcut is invisible, and somebody on a laptop trackpad should not have to
 * guess it.
 */
export function Lightbox({
  images,
  startIndex,
  onClose,
}: {
  images: LightboxImage[];
  startIndex: number;
  onClose: () => void;
}) {
  const [index, setIndex] = React.useState(startIndex);
  const closeRef = React.useRef<HTMLButtonElement>(null);

  // Wrapping rather than stopping at the ends: with three photos, "next" from the last
  // one obviously means the first, and a dead arrow key reads as the page being broken.
  const count = images.length;
  const go = React.useCallback(
    (step: number) => setIndex((i) => (i + step + count) % count),
    [count],
  );

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowLeft') go(-1);
      else if (event.key === 'ArrowRight') go(1);
      else return;
      // Otherwise the arrow keys also scroll the page behind the overlay, so closing
      // leaves the reader somewhere they did not navigate to.
      event.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  // The page behind must not scroll under the overlay.
  React.useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Move focus in, so the arrow keys reach this and not whatever was focused before.
  React.useEffect(() => closeRef.current?.focus(), []);

  const current = images[index];
  if (!current) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} of ${count}: ${current.filename}`}
      // Closes on the backdrop, which is what clicking "off" a photo means everywhere
      // else. The image and the controls stop the event so they do not close it.
      onClick={onClose}
      className="fixed inset-0 z-50 flex flex-col bg-black/90 backdrop-blur-sm"
    >
      <div className="flex items-center justify-between gap-2 p-3 text-white">
        <span className="truncate text-xs opacity-80">{current.filename}</span>
        <div className="flex items-center gap-1">
          {count > 1 && (
            <span className="at-num mr-2 text-xs opacity-80">
              {index + 1} / {count}
            </span>
          )}
          <a
            href={current.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label="Open the full-size photo in a new tab"
            className="rounded p-1.5 opacity-80 hover:bg-white/10 hover:opacity-100"
          >
            <Download className="size-4" />
          </a>
          <Hint label={'Close'}><button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 opacity-80 hover:bg-white/10 hover:opacity-100"
          >
            <X className="size-4" />
          </button></Hint>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-2 pb-3">
        {count > 1 && (
          <Arrow side="left" onClick={() => go(-1)} />
        )}

        <img
          src={current.url}
          alt={current.filename}
          onClick={(e) => e.stopPropagation()}
          className="max-h-full max-w-full rounded object-contain"
        />

        {count > 1 && <Arrow side="right" onClick={() => go(1)} />}
      </div>

      {count > 1 && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="flex justify-center gap-1.5 overflow-x-auto px-3 pb-3"
        >
          {images.map((image, i) => (
            <Hint label={`Show photo ${i + 1}`}><button
              key={image.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Show photo ${i + 1}`}
              aria-current={i === index}
              className={cn(
                'shrink-0 overflow-hidden rounded border-2 transition-opacity',
                i === index ? 'border-white' : 'border-transparent opacity-50 hover:opacity-90',
              )}
            >
              <img src={image.url} alt="" className="size-12 object-cover" />
            </button></Hint>
          ))}
        </div>
      )}

      {count > 1 && (
        <p className="pb-3 text-center text-[11px] text-white/50">
          Use ← and → to move between photos, Esc to close
        </p>
      )}
    </div>
  );
}

function Arrow({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <Hint label={side === 'left' ? 'Previous photo' : 'Next photo'}><button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={side === 'left' ? 'Previous photo' : 'Next photo'}
      className={cn(
        'absolute top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white opacity-80 transition hover:bg-white/20 hover:opacity-100',
        side === 'left' ? 'left-2' : 'right-2',
      )}
    >
      <Icon className="size-5" />
    </button></Hint>
  );
}

/**
 * Wiring for a strip of thumbnails that opens into the viewer.
 *
 * Only images take part. A PDF has nothing to show in a slider, so it keeps opening in
 * a tab where the browser's own reader handles it.
 */
export function useLightbox(images: LightboxImage[]) {
  const [openAt, setOpenAt] = React.useState<number | null>(null);

  return {
    open: (id: string) => {
      const index = images.findIndex((image) => image.id === id);
      if (index >= 0) setOpenAt(index);
    },
    element:
      openAt === null ? null : (
        <Lightbox images={images} startIndex={openAt} onClose={() => setOpenAt(null)} />
      ),
  };
}
